import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, CircularProgress } from '@mui/material';
import { useSnackbar } from 'notistack';
import moment from 'moment-jalaali';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CampaignIcon from '@mui/icons-material/Campaign';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CancelIcon from '@mui/icons-material/Cancel';

import * as blogAdService from '../services/blogAd.service';
import type { BlogAdPayload, BlogAdSlot } from '../services/blogAd.service';
import imageUploadService from '../services/imageUpload.service';
import { ConfirmDialog } from '../components/common';
import { toPersianDigits } from '../components/common/JalaliDatePicker';

import './EntityManagement.css';

// =====================================================
// Options
// =====================================================

// هر جایگاه یک «باکس فیزیکی ثابت» است (مثل اسلات‌های بنر فروشگاه) و یک تبلیغ نمایش می‌دهد.
// تنها استثنا blog_list_inline است که چرخشی/چندتایی است.
interface SlotOption {
  value: BlogAdSlot;
  group: string;
  label: string;
  size: string;
  /** نسبت عرض به ارتفاعِ پیشنهادی — برای هشدار هنگام آپلود تصویر با نسبت متفاوت */
  ratio: number;
}

/** تصویر بزرگ‌تر از این حجم، سرعت صفحهٔ بلاگ را پایین می‌آورد */
const MAX_RECOMMENDED_BYTES = 600 * 1024;

const SLOT_OPTIONS: SlotOption[] = [
  // —— همه صفحات بلاگ ——
  { value: 'blog_top_strip', group: 'همه صفحات بلاگ', label: 'نوار بالای همه صفحات (قابل بستن)', size: 'تمام‌عرض × ۹۰ — پیشنهادی ۱۲۰۰×۹۰', ratio: 1200 / 90 },
  // —— صفحه اصلی بلاگ ——
  { value: 'blog_home_top', group: 'صفحه اصلی بلاگ', label: 'بنر عریض بالای صفحه (زیر موضوعات)', size: '۹۷۰×۹۰ — نسبت ۱۰:۱', ratio: 10 },
  { value: 'blog_home_double_right', group: 'صفحه اصلی بلاگ', label: 'باکس دوتایی بالایی — راست', size: '۳۰۰×۲۵۰', ratio: 1.2 },
  { value: 'blog_home_double_left', group: 'صفحه اصلی بلاگ', label: 'باکس دوتایی بالایی — چپ', size: '۳۰۰×۲۵۰', ratio: 1.2 },
  { value: 'blog_home_bottom_right', group: 'صفحه اصلی بلاگ', label: 'باکس دوتایی پایینی (زیر پیشنهاد سردبیر) — راست', size: '۳۰۰×۲۵۰', ratio: 1.2 },
  { value: 'blog_home_bottom_left', group: 'صفحه اصلی بلاگ', label: 'باکس دوتایی پایینی (زیر پیشنهاد سردبیر) — چپ', size: '۳۰۰×۲۵۰', ratio: 1.2 },
  // —— آرشیو مقالات ——
  { value: 'blog_list_sidebar_r1', group: 'آرشیو مقالات (مرور مطالب)', label: 'سایدبار فیلترها — باکس ۱ (بالا)', size: '۳۰۰×۲۵۰', ratio: 1.2 },
  { value: 'blog_list_sidebar_r2', group: 'آرشیو مقالات (مرور مطالب)', label: 'سایدبار فیلترها — باکس ۲ (وسط)', size: '۳۰۰×۲۵۰', ratio: 1.2 },
  { value: 'blog_list_sidebar_r3', group: 'آرشیو مقالات (مرور مطالب)', label: 'سایدبار فیلترها — باکس ۳ (پایین)', size: '۳۰۰×۲۵۰', ratio: 1.2 },
  { value: 'blog_list_inline', group: 'آرشیو مقالات (مرور مطالب)', label: 'بنر بین ردیف‌های فهرست (هر ۵ مطلب؛ آرشیو، دسته‌بندی، نویسنده) — چرخشی', size: '۷۲۸×۹۰ — چند تبلیغ مجاز', ratio: 728 / 90 },
  // —— صفحه دسته‌بندی ——
  { value: 'blog_category_sidebar_right', group: 'صفحه دسته‌بندی', label: 'باکس دوتایی (بالای پیشنهاد سردبیر) — راست', size: '۳۰۰×۲۵۰', ratio: 1.2 },
  { value: 'blog_category_sidebar_left', group: 'صفحه دسته‌بندی', label: 'باکس دوتایی (بالای پیشنهاد سردبیر) — چپ', size: '۳۰۰×۲۵۰', ratio: 1.2 },
  // —— صفحه مقاله ——
  { value: 'blog_article_top', group: 'صفحه مقاله', label: 'بنر داخل مقاله (زیر خلاصه) — دسکتاپ و موبایل', size: '۹۷۰×۱۲۰ — نسبت ۸:۱', ratio: 8 },
  // سایدبار مقاله: ۳ بنر هم‌اندازه زیر هم که با اسکرول می‌چسبند (هر ۳ تا می‌مانند)؛ در موبایل همین ۳ تا داخل متن می‌آیند
  { value: 'blog_article_sidebar', group: 'صفحه مقاله', label: 'سایدبار (چسبان) — بنر ۱ (بالا) / موبایل: تبلیغ ۱ داخل متن', size: '۳۲۰×۱۵۰ — نسبت ۲.۱', ratio: 320 / 150 },
  { value: 'blog_article_sidebar_2', group: 'صفحه مقاله', label: 'سایدبار (چسبان) — بنر ۲ (وسط) / موبایل: تبلیغ ۲ داخل متن', size: '۳۲۰×۱۵۰ — نسبت ۲.۱', ratio: 320 / 150 },
  { value: 'blog_article_sidebar_wide_1', group: 'صفحه مقاله', label: 'سایدبار (چسبان) — بنر ۳ (پایین) / موبایل: تبلیغ ۳ داخل متن', size: '۳۲۰×۱۵۰ — نسبت ۲.۱', ratio: 320 / 150 },
];

// گروه‌ها به ترتیبِ ظاهرشدن (برای optgroup)
const SLOT_GROUPS = Array.from(new Set(SLOT_OPTIONS.map((o) => o.group)));

const getSlotOption = (slot: string) => SLOT_OPTIONS.find((o) => o.value === slot);
const slotLabel = (slot: string) => {
  const o = getSlotOption(slot);
  return o ? `${o.group} — ${o.label}` : slot;
};
const slotSize = (slot: string) => getSlotOption(slot)?.size || '';

// جایگاه‌های چرخشی (چند تبلیغ مجاز). بقیه فقط یک تبلیغ نمایش می‌دهند.
const ROTATING_SLOTS: BlogAdSlot[] = ['blog_list_inline'];
const isRotating = (slot: BlogAdSlot) => ROTATING_SLOTS.includes(slot);

const emptyPayload = (): BlogAdPayload => ({
  name: '',
  slot: 'blog_home_top',
  image: '',
  mobileImage: '',
  imageAlt: '',
  linkUrl: '',
  openInNewTab: true,
  startDate: '',
  endDate: '',
  order: 0,
  isActive: true,
});

// =====================================================
// Component
// =====================================================

const BlogAdManagement: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  // List state
  const [ads, setAds] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Filters
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | 'true' | 'false'>('');
  const [slotFilter, setSlotFilter] = useState<string>('');

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [formData, setFormData] = useState<BlogAdPayload>(emptyPayload());
  const [formError, setFormError] = useState('');
  const [uploadingField, setUploadingField] = useState<'image' | 'mobileImage' | null>(null);
  // ابعاد/حجم تصویر اصلیِ آپلودشده در همین جلسه — برای هشدار نسبت و حجم
  const [imageMeta, setImageMeta] = useState<{ width: number; height: number; bytes: number } | null>(null);
  // همهٔ تبلیغ‌ها (بدون فیلتر) فقط برای کارت‌های آمار؛ لیست جدول صفحه‌بندی/فیلتر می‌شود
  const [allAds, setAllAds] = useState<any[]>([]);
  // تبلیغ‌های موجود در همان جایگاهِ انتخاب‌شده (برای نمایش باکس‌های پر/خالی)
  const [slotAds, setSlotAds] = useState<any[]>([]);
  const [slotAdsLoading, setSlotAdsLoading] = useState(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);

  // ----- Fetch -----
  const fetchAds = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page, limit: 20 };
      if (searchTerm) params.search = searchTerm;
      if (statusFilter !== '') params.isActive = statusFilter;
      if (slotFilter) params.slot = slotFilter;
      const [res, allRes]: any[] = await Promise.all([
        blogAdService.listBlogAds(params),
        blogAdService.listBlogAds({ limit: 500 }),
      ]);
      const data = res?.data || res;
      setAds(data.docs || []);
      setTotalPages(data.totalPages || 1);
      setTotal(data.totalDocs || data.totalCount || 0);
      const allData = allRes?.data || allRes;
      setAllAds(allData.docs || []);
    } catch (err: any) {
      enqueueSnackbar('خطا در بارگذاری تبلیغات', { variant: 'error' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, searchTerm, statusFilter, slotFilter, enqueueSnackbar]);

  useEffect(() => {
    fetchAds();
  }, [fetchAds]);

  // بارگذاری تبلیغ‌های همان جایگاه هنگام باز بودن دیالوگ یا تغییر جایگاه
  useEffect(() => {
    if (!dialogOpen) return;
    let cancelled = false;
    (async () => {
      setSlotAdsLoading(true);
      try {
        const res: any = await blogAdService.listBlogAds({ slot: formData.slot, limit: 100 });
        const data = res?.data || res;
        const docs: any[] = data.docs || [];
        if (cancelled) return;
        setSlotAds(docs);
        // در حالت «ایجاد» برای جایگاه چرخشی، order را به انتهای صف ببر تا تبلیغ جدید آخرِ چرخش بیفتد
        if (!editingId && isRotating(formData.slot)) {
          const maxOrder = docs.reduce((m, a) => Math.max(m, a.order ?? 0), -1);
          setFormData((p) => ({ ...p, order: maxOrder + 1 }));
        }
      } catch {
        if (!cancelled) setSlotAds([]);
      } finally {
        if (!cancelled) setSlotAdsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dialogOpen, formData.slot, editingId]);

  // ----- Handlers -----
  const handleSearch = () => {
    setSearchTerm(searchInput.trim());
    setPage(1);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSearchInput('');
    setStatusFilter('');
    setSlotFilter('');
    setPage(1);
  };

  const hasActiveFilters = !!(searchTerm || statusFilter || slotFilter);

  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyPayload());
    setImageMeta(null);
    setFormError('');
    setDialogOpen(true);
  };

  const openEdit = (ad: any) => {
    setEditingId(ad._id);
    setFormData({
      name: ad.name || '',
      slot: ad.slot || 'blog_home_top',
      image: ad.image || '',
      mobileImage: ad.mobileImage || '',
      imageAlt: ad.imageAlt || '',
      linkUrl: ad.linkUrl || '',
      openInNewTab: ad.openInNewTab ?? true,
      startDate: ad.startDate ? ad.startDate.split('T')[0] : '',
      endDate: ad.endDate ? ad.endDate.split('T')[0] : '',
      order: ad.order ?? 0,
      isActive: ad.isActive ?? true,
    });
    setImageMeta(null);
    setFormError('');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (actionLoading) return;
    setDialogOpen(false);
  };

  const validateForm = (): string | null => {
    if (!formData.name?.trim()) return 'نام تبلیغ الزامی است.';
    if (!formData.slot) return 'جایگاه تبلیغ را انتخاب کنید.';
    if (!formData.image) return 'تصویر تبلیغ الزامی است.';
    if (formData.startDate && formData.endDate && formData.endDate <= formData.startDate)
      return 'تاریخ پایان باید بعد از تاریخ شروع باشد.';
    return null;
  };

  const handleSave = async () => {
    const err = validateForm();
    if (err) {
      setFormError(err);
      enqueueSnackbar(err, { variant: 'warning' });
      return;
    }
    setActionLoading(true);
    try {
      const payload: BlogAdPayload = {
        ...formData,
        mobileImage: formData.mobileImage || undefined,
        startDate: formData.startDate || undefined,
        endDate: formData.endDate || undefined,
      };
      if (editingId) {
        await blogAdService.updateBlogAd(editingId, payload);
        enqueueSnackbar('تبلیغ بروزرسانی شد', { variant: 'success' });
      } else {
        await blogAdService.createBlogAd(payload);
        enqueueSnackbar('تبلیغ ایجاد شد', { variant: 'success' });
      }
      setDialogOpen(false);
      fetchAds();
    } catch (err: any) {
      const msg =
        err?.response?.data?.errors?.[0] ||
        err?.response?.data?.message ||
        'خطا در ذخیره';
      setFormError(msg);
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggle = async (ad: any) => {
    try {
      await blogAdService.toggleBlogAdActive(ad._id, !ad.isActive);
      enqueueSnackbar(ad.isActive ? 'تبلیغ غیرفعال شد' : 'تبلیغ فعال شد', { variant: 'success' });
      fetchAds();
    } catch {
      enqueueSnackbar('خطا در تغییر وضعیت', { variant: 'error' });
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await blogAdService.deleteBlogAd(deleteTarget._id);
      enqueueSnackbar('تبلیغ حذف شد', { variant: 'success' });
      setDeleteTarget(null);
      fetchAds();
    } catch {
      enqueueSnackbar('خطا در حذف', { variant: 'error' });
    }
  };

  /** ابعاد واقعی فایل تصویر (قبل از آپلود) — برای هشدار نسبت/حجم */
  const readImageSize = (file: File) =>
    new Promise<{ width: number; height: number } | null>((resolve) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
        URL.revokeObjectURL(url);
      };
      img.onerror = () => {
        resolve(null);
        URL.revokeObjectURL(url);
      };
      img.src = url;
    });

  const uploadImage = async (field: 'image' | 'mobileImage', file: File) => {
    if (!file.type.startsWith('image/')) {
      enqueueSnackbar('فقط فایل تصویری مجاز است', { variant: 'warning' });
      return;
    }
    setUploadingField(field);
    try {
      const size = field === 'image' ? await readImageSize(file) : null;
      const result = await imageUploadService.uploadImage(file, 'blog-ads');
      if (result?.url) {
        setFormData((p) => ({ ...p, [field]: result.url }));
        if (field === 'image') setImageMeta(size ? { ...size, bytes: file.size } : null);
        enqueueSnackbar('تصویر آپلود شد', { variant: 'success' });
      } else {
        enqueueSnackbar('خطا در آپلود تصویر', { variant: 'error' });
      }
    } catch {
      enqueueSnackbar('خطا در آپلود تصویر', { variant: 'error' });
    } finally {
      setUploadingField(null);
    }
  };

  // ----- Stats -----
  const stats = useMemo(() => {
    const now = new Date();
    let active = 0,
      inactive = 0,
      scheduled = 0,
      expired = 0;
    allAds.forEach((a) => {
      if (!a.isActive) inactive++;
      else if (a.startDate && new Date(a.startDate) > now) scheduled++;
      else if (a.endDate && new Date(a.endDate) < now) expired++;
      else active++;
    });
    return { total: allAds.length || total, active, inactive, scheduled, expired };
  }, [allAds, total]);

  const statusTag = (ad: any) => {
    const now = new Date();
    if (!ad.isActive) return <span className="tag tag-sm tag-inactive">غیرفعال</span>;
    if (ad.startDate && new Date(ad.startDate) > now)
      return <span className="tag tag-sm tag-platform">زمان‌بندی شده</span>;
    if (ad.endDate && new Date(ad.endDate) < now)
      return <span className="tag tag-sm tag-inactive">منقضی</span>;
    return <span className="tag tag-sm tag-active">فعال</span>;
  };

  const selectedOption = getSlotOption(formData.slot);
  const rotating = isRotating(formData.slot);
  // تبلیغ‌های فعالِ دیگری که همین جایگاهِ تک‌باکس را از قبل اشغال کرده‌اند
  const occupantAds = slotAds.filter((a) => a._id !== editingId && a.isActive);
  const slotOccupied = !rotating && occupantAds.length > 0;

  // هشدارهای تصویر: انحراف نسبت از پیشنهاد جایگاه (بیش از ۱۵٪) و حجم زیاد
  const imageWarnings = useMemo(() => {
    const out: string[] = [];
    if (!imageMeta || !selectedOption) return out;
    const actual = imageMeta.width / imageMeta.height;
    const dev = Math.abs(actual - selectedOption.ratio) / selectedOption.ratio;
    if (dev > 0.15) {
      out.push(
        `نسبت تصویر (${toPersianDigits(String(imageMeta.width))}×${toPersianDigits(String(imageMeta.height))}) با نسبت پیشنهادی این جایگاه فرق دارد؛ تصویر بدون برش ولی با ارتفاع متفاوت از باکس‌های کناری نمایش داده می‌شود.`
      );
    }
    if (imageMeta.bytes > MAX_RECOMMENDED_BYTES) {
      out.push(
        `حجم تصویر ${toPersianDigits(String(Math.round(imageMeta.bytes / 1024)))} کیلوبایت است؛ برای سرعت صفحه، زیر ${toPersianDigits(String(MAX_RECOMMENDED_BYTES / 1024))} کیلوبایت توصیه می‌شود.`
      );
    }
    return out;
  }, [imageMeta, selectedOption]);

  // =====================================================
  // Render
  // =====================================================

  return (
    <div className="entity-page">
      {/* Header */}
      <div className="entity-header">
        <div>
          <h1 className="entity-title">تبلیغات بلاگ</h1>
          <p className="entity-subtitle">مدیریت بنرهای تبلیغاتی جایگاه‌های بخش بلاگ و مقالات</p>
        </div>
        <div className="header-actions">
          <button className="header-btn refresh" onClick={fetchAds} title="بروزرسانی">
            <RefreshIcon fontSize="small" />
          </button>
          <button className="header-btn primary" onClick={openCreate}>
            <AddIcon fontSize="small" />
            تبلیغ جدید
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="entity-stats">
        <div className="entity-stat-card">
          <div className="entity-stat-icon blue"><CampaignIcon /></div>
          <div>
            <p className="entity-stat-label">کل تبلیغات</p>
            <p className="entity-stat-value">{toPersianDigits(String(stats.total))}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon green"><CheckCircleIcon /></div>
          <div>
            <p className="entity-stat-label">فعال</p>
            <p className="entity-stat-value">{toPersianDigits(String(stats.active))}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon orange"><ScheduleIcon /></div>
          <div>
            <p className="entity-stat-label">زمان‌بندی شده</p>
            <p className="entity-stat-value">{toPersianDigits(String(stats.scheduled))}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon red"><CancelIcon /></div>
          <div>
            <p className="entity-stat-label">غیرفعال / منقضی</p>
            <p className="entity-stat-value">{toPersianDigits(String(stats.inactive + stats.expired))}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="entity-filters">
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">
              <SearchIcon fontSize="small" />
              جستجو
            </label>
            <div className="filter-search-wrap">
              <input
                className="filter-field"
                type="text"
                placeholder="جستجو در نام تبلیغ..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <span className="filter-search-icon">
                <SearchIcon fontSize="small" />
              </span>
            </div>
          </div>

          <div className="filter-group">
            <label className="filter-label">جایگاه</label>
            <select
              className="filter-field"
              value={slotFilter}
              onChange={(e) => {
                setSlotFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">همه جایگاه‌ها</option>
              {SLOT_GROUPS.map((group) => (
                <optgroup key={group} label={group}>
                  {SLOT_OPTIONS.filter((o) => o.group === group).map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">وضعیت</label>
            <select
              className="filter-field"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as any);
                setPage(1);
              }}
            >
              <option value="">همه وضعیت‌ها</option>
              <option value="true">فعال</option>
              <option value="false">غیرفعال</option>
            </select>
          </div>

          <div className="filter-group filter-group-actions">
            <button className="filter-btn" onClick={handleSearch}>
              <SearchIcon fontSize="small" />
              اعمال
            </button>
            {hasActiveFilters && (
              <button className="filter-btn outline" onClick={clearFilters}>پاک</button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="entity-table-wrapper">
        {loading && ads.length === 0 ? (
          <div className="entity-loading"><CircularProgress size={32} /></div>
        ) : ads.length === 0 ? (
          <div className="entity-empty">
            <div className="entity-empty-icon"><CampaignIcon style={{ fontSize: 48 }} /></div>
            <p className="entity-empty-text">هیچ تبلیغی یافت نشد</p>
            <button className="header-btn primary" style={{ marginTop: 12 }} onClick={openCreate}>
              <AddIcon fontSize="small" />
              ایجاد اولین تبلیغ
            </button>
          </div>
        ) : (
          <>
            <table className="entity-table">
              <thead>
                <tr>
                  <th>تبلیغ</th>
                  <th>جایگاه</th>
                  <th>وضعیت</th>
                  <th>نمایش / کلیک</th>
                  <th>ابعاد</th>
                  <th>تاریخ نمایش</th>
                  <th style={{ width: 130 }}>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {ads.map((ad) => {
                  const impressions = ad.analytics?.impressions || 0;
                  const clicks = ad.analytics?.clicks || 0;
                  return (
                    <tr key={ad._id}>
                      <td>
                        <div className="entity-info-cell">
                          <div className="entity-image">
                            {ad.image ? (
                              <img src={ad.image} alt={ad.name} />
                            ) : (
                              <CampaignIcon />
                            )}
                          </div>
                          <div className="entity-info">
                            <span className="entity-name">{ad.name}</span>
                            {ad.linkUrl && (
                              <span className="entity-desc" style={{ direction: 'ltr' }}>{ad.linkUrl}</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="tag tag-sm tag-platform">{slotLabel(ad.slot)}</span>
                      </td>
                      <td>{statusTag(ad)}</td>
                      <td>
                        <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
                          {toPersianDigits(String(impressions))} / {toPersianDigits(String(clicks))}
                        </span>
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{slotSize(ad.slot)}</td>
                      <td style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                        {ad.startDate || ad.endDate ? (
                          <>
                            {ad.startDate
                              ? toPersianDigits(moment(ad.startDate).format('jYYYY/jMM/jDD'))
                              : '—'}
                            <br />
                            {ad.endDate
                              ? toPersianDigits(moment(ad.endDate).format('jYYYY/jMM/jDD'))
                              : 'نامحدود'}
                          </>
                        ) : (
                          'نامحدود'
                        )}
                      </td>
                      <td>
                        <div className="actions-cell">
                          <button
                            className={`action-btn toggle ${ad.isActive ? 'active' : ''}`}
                            onClick={() => handleToggle(ad)}
                            title={ad.isActive ? 'غیرفعال کردن' : 'فعال کردن'}
                          >
                            {ad.isActive ? <VisibilityIcon /> : <VisibilityOffIcon />}
                          </button>
                          <button className="action-btn edit" onClick={() => openEdit(ad)} title="ویرایش">
                            <EditIcon />
                          </button>
                          <button className="action-btn delete" onClick={() => setDeleteTarget(ad)} title="حذف">
                            <DeleteIcon />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div className="entity-pagination" style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: 16 }}>
                <button
                  className="filter-btn outline"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  قبلی
                </button>
                <span style={{ alignSelf: 'center', fontSize: 13, color: 'var(--color-text-secondary)' }}>
                  صفحه {toPersianDigits(String(page))} از {toPersianDigits(String(totalPages))}
                </span>
                <button
                  className="filter-btn outline"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  بعدی
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* ============== Create / Edit Dialog ============== */}
      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        maxWidth="sm"
        fullWidth
        className="blogad-edit-dialog"
      >
        <div className="edit-dialog-title">
          <span>{editingId ? 'ویرایش تبلیغ' : 'تبلیغ جدید'}</span>
          <button className="details-close" onClick={closeDialog}>
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div style={{ padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 16, flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <div className="edit-field">
            <label>نام تبلیغ (داخلی) *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
              placeholder="مثلاً: کمپین تابستانه — سایدبار بلاگ"
            />
          </div>

          <div className="edit-field">
            <label>جایگاه نمایش *</label>
            <select
              value={formData.slot}
              onChange={(e) => setFormData((p) => ({ ...p, slot: e.target.value as BlogAdSlot }))}
            >
              {SLOT_GROUPS.map((group) => (
                <optgroup key={group} label={group}>
                  {SLOT_OPTIONS.filter((o) => o.group === group).map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
            {selectedOption && (
              <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                ابعاد پیشنهادی: {selectedOption.size}
                {rotating
                  ? ' — این جایگاه چرخشی است و چند تبلیغ می‌پذیرد.'
                  : ' — این جایگاه فقط «یک» تبلیغ نمایش می‌دهد.'}
                {' '}تصویر در سایت با نسبت اصلی خودش و بدون برش نمایش داده می‌شود.
              </span>
            )}
            {slotAdsLoading ? (
              <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>در حال بررسی این جایگاه…</span>
            ) : slotOccupied ? (
              <span style={{ fontSize: 12, color: 'var(--color-warning, #d97706)', display: 'flex', alignItems: 'center', gap: 4 }}>
                ⚠️ این جایگاه از قبل توسط «{occupantAds[0].name}» اشغال شده است. اگر این تبلیغ هم فعال باشد، فقط یکی نمایش داده می‌شود؛ بهتر است ابتدا تبلیغ قبلی را غیرفعال یا حذف کنید.
              </span>
            ) : null}
          </div>

          {/* تصویر اصلی */}
          <div className="edit-field">
            <label>تصویر تبلیغ *</label>
            {formData.image && (
              <img
                src={formData.image}
                alt={formData.imageAlt || formData.name}
                style={{ maxWidth: '100%', maxHeight: 180, borderRadius: 8, objectFit: 'contain', border: '1px solid var(--color-border)' }}
              />
            )}
            <label
              className="filter-btn outline"
              style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, width: 'fit-content' }}
            >
              {uploadingField === 'image' ? <CircularProgress size={16} /> : null}
              {formData.image ? 'تغییر تصویر' : 'آپلود تصویر'}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => e.target.files?.[0] && uploadImage('image', e.target.files[0])}
              />
            </label>
            {imageWarnings.map((w) => (
              <span key={w} style={{ fontSize: 12, color: 'var(--color-warning, #d97706)' }}>⚠️ {w}</span>
            ))}
          </div>

          {/* تصویر موبایل (اختیاری) */}
          <div className="edit-field">
            <label>تصویر موبایل (اختیاری)</label>
            {formData.mobileImage && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <img
                  src={formData.mobileImage}
                  alt=""
                  style={{ maxWidth: '60%', maxHeight: 120, borderRadius: 8, objectFit: 'contain', border: '1px solid var(--color-border)' }}
                />
                <button
                  className="filter-btn outline"
                  onClick={() => setFormData((p) => ({ ...p, mobileImage: '' }))}
                >
                  حذف
                </button>
              </div>
            )}
            <label
              className="filter-btn outline"
              style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, width: 'fit-content' }}
            >
              {uploadingField === 'mobileImage' ? <CircularProgress size={16} /> : null}
              {formData.mobileImage ? 'تغییر تصویر موبایل' : 'آپلود تصویر موبایل'}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => e.target.files?.[0] && uploadImage('mobileImage', e.target.files[0])}
              />
            </label>
          </div>

          <div className="edit-field">
            <label>متن جایگزین تصویر (alt)</label>
            <input
              type="text"
              value={formData.imageAlt}
              onChange={(e) => setFormData((p) => ({ ...p, imageAlt: e.target.value }))}
              placeholder="توضیح کوتاه تصویر برای سئو و دسترس‌پذیری"
            />
          </div>

          <div className="edit-field">
            <label>لینک کلیک (اختیاری)</label>
            <input
              type="text"
              dir="ltr"
              value={formData.linkUrl}
              onChange={(e) => setFormData((p) => ({ ...p, linkUrl: e.target.value }))}
              placeholder="https://... یا /products/..."
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="edit-field">
              <label>تاریخ شروع (اختیاری)</label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData((p) => ({ ...p, startDate: e.target.value }))}
              />
            </div>
            <div className="edit-field">
              <label>تاریخ پایان (اختیاری)</label>
              <input
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData((p) => ({ ...p, endDate: e.target.value }))}
              />
            </div>
          </div>

          {/* ترتیب چرخش فقط برای جایگاه چرخشی معنا دارد */}
          {rotating && (
            <div className="edit-field">
              <label>ترتیب چرخش</label>
              <input
                type="number"
                min={0}
                max={1000}
                value={formData.order}
                onChange={(e) => setFormData((p) => ({ ...p, order: parseInt(e.target.value) || 0 }))}
              />
              <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                عدد کوچک‌تر زودتر در چرخشِ بین مقالات نمایش داده می‌شود.
              </span>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, alignItems: 'center' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={!!formData.openInNewTab}
                onChange={(e) => setFormData((p) => ({ ...p, openInNewTab: e.target.checked }))}
              />
              باز شدن در تب جدید
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={!!formData.isActive}
                onChange={(e) => setFormData((p) => ({ ...p, isActive: e.target.checked }))}
              />
              فعال
            </label>
          </div>

          {formError && (
            <div style={{ color: 'var(--color-error, #dc2626)', fontSize: 13 }}>{formError}</div>
          )}
        </div>

        <div className="edit-dialog-actions">
          <button className="filter-btn outline" onClick={closeDialog} disabled={actionLoading}>
            انصراف
          </button>
          <button className="header-btn primary" onClick={handleSave} disabled={actionLoading || uploadingField !== null}>
            {actionLoading ? <CircularProgress size={18} style={{ color: '#fff' }} /> : editingId ? 'ذخیره تغییرات' : 'ایجاد تبلیغ'}
          </button>
        </div>
      </Dialog>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="حذف تبلیغ"
        message={`آیا از حذف تبلیغ «${deleteTarget?.name || ''}» مطمئن هستید؟`}
        confirmText="حذف"
        cancelText="انصراف"
        confirmColor="error"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default BlogAdManagement;
